import Modal, { ModalProps } from "@mui/material/Modal";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { EmtIcon } from "./EmtIcon";
import { EmtTheme } from "../types";
import { useMobile } from "../hooks/useMobile";

export type EmtModalProps = ModalProps & {
  /**
   * @default default
   */
  variant?: "default" | "large" | "empty" | "carousel";
  nonce?: string;
};

const DefaultInnerBox = styled(Box)(
  ({
    theme,
    showFullScreenMobile,
  }: {
    theme?: EmtTheme;
    showFullScreenMobile?: boolean;
  }) => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    borderRadius: "24px",
    background: theme?.palette.common.white,
    width: theme?.breakpoints.values.sm - 48,
    [theme?.breakpoints.down("sm")]: {
      width: "90%",
    },
    ...(showFullScreenMobile && {
      width: "100vw!important",
      height: "100vh!important",
      maxHeight: "100vh!important",
      borderRadius: "0!important",
    }),
  })
);

const LargeInnerBox = styled(DefaultInnerBox)(
  ({
    theme,
    showFullScreenMobile = false,
  }: {
    theme?: EmtTheme;
    showFullScreenMobile?: boolean;
  }) => ({
    width: "70%",
    [theme?.breakpoints.down("lg")]: {
      width: "80%",
    },
    [theme?.breakpoints.down("sm")]: {
      width: "90%",
    },
    ...(showFullScreenMobile && {
      width: "100vw!important",
      height: "100vh!important",
      maxHeight: "100vh!important",
      borderRadius: "0!important",
    }),
  })
);

const CarouselInnerBox = styled(LargeInnerBox)(
  ({
    theme,
    showFullScreenMobile,
  }: {
    theme?: EmtTheme;
    showFullScreenMobile?: boolean;
  }) => ({
    background: theme?.palette.greyGradient[50],
    "& .emt-icons.emt-x": {
      color: theme?.palette.common.white,
      position: "absolute",
      top: -32,
      right: 0,
      fontSize: "22px",
    },
    [theme?.breakpoints.down("sm")]: {
      borderRadius: "0",
      width: "100%",
      height: "100%",
      "& .emt-icons.emt-x": {
        color: theme?.palette.greyGradient[550],
        position: "absolute",
        top: 25,
        right: 25,
        fontSize: "16px",
        fontWeight: 100,
      },
      "& .swiper-pagination": {
        bottom: 0,
      },
    },
    ...(showFullScreenMobile && {
      width: "100vw!important",
      height: "100dvh!important",
      maxHeight: "100dvh!important",
      borderRadius: "0!important",
    }),
  })
);

const DefaultChildrenBox = styled(Box)(({ theme }) => ({
  padding: "24px",
}));

const CarouselChildrenBox = styled(Box)(({ theme }) => ({
  padding: "24px",
  [theme.breakpoints.down("sm")]: {
    padding: "20px 25px",
    height: "100%",
  },
}));

const CloseIcon = styled(EmtIcon)(({ theme }: { theme?: EmtTheme }) => ({
  position: "absolute",
  top: "20px",
  right: "20px",
  color: theme?.palette.greyGradient[450],
  cursor: "pointer",
  fontSize: "20px",
  zIndex: 1,
}));

export function EmtModal(
  props: EmtModalProps & { fullScreenMobile?: boolean; isMobile?: boolean }
) {
  const {
    children,
    variant = "default",
    fullScreenMobile = false,
    ...rest
  } = props;
  const [isMobile] = useMobile();
  const showFullScreenMobile = fullScreenMobile && isMobile;
  return (
    <Modal {...rest} disableAutoFocus disableEnforceFocus>
      {variant === "empty" ? (
        children
      ) : variant === "large" ? (
        <LargeInnerBox
          showFullScreenMobile={showFullScreenMobile}
          className="inner-box"
        >
          {rest.onClose && (
            <CloseIcon
              className="emt-x EmtReactNativeWebViewMarginTop"
              onClick={(e: React.MouseEvent<SVGSVGElement>) =>
                rest.onClose ? rest.onClose(e, "backdropClick") : null
              }
            />
          )}
          <DefaultChildrenBox className="children-box">
            {children}
          </DefaultChildrenBox>
        </LargeInnerBox>
      ) : variant === "carousel" ? (
        <CarouselInnerBox
          showFullScreenMobile={showFullScreenMobile}
          className="inner-box"
        >
          {rest.onClose && (
            <CloseIcon
              className="emt-x EmtReactNativeWebViewMarginTop"
              onClick={(e: React.MouseEvent<SVGSVGElement>) =>
                rest.onClose ? rest.onClose(e, "backdropClick") : null
              }
            />
          )}
          <CarouselChildrenBox className="children-box">
            {children}
          </CarouselChildrenBox>
        </CarouselInnerBox>
      ) : (
        <DefaultInnerBox
          className={`inner-box${
            showFullScreenMobile ? " EmtReactNativeWebViewPaddingBottom" : ""
          }`}
          sx={{
            maxHeight: "90vh",
            overflowY: "auto",
          }}
          showFullScreenMobile={showFullScreenMobile}
        >
          {rest.onClose && (
            <CloseIcon
              className={`emt-x${
                showFullScreenMobile ? " EmtReactNativeWebViewMarginTop" : ""
              }`}
              onClick={(e: React.MouseEvent<SVGSVGElement>) =>
                rest.onClose ? rest.onClose(e, "backdropClick") : null
              }
            />
          )}
          <DefaultChildrenBox
            className={`children-box${
              showFullScreenMobile ? " EmtReactNativeWebViewMarginTop" : ""
            }`}
          >
            {children}
          </DefaultChildrenBox>
        </DefaultInnerBox>
      )}
    </Modal>
  );
}
